import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, Globe, Menu, X, ArrowRight, ArrowLeft, Check, Send, Building2, Award, Loader2, VolumeX, Volume2, Mic, Square, Wand2, Users, Sparkles, Handshake, ChevronRight, Home as HomeIcon, Search, UserPlus, BadgeCheck, ExternalLink, KeyRound, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { SoundWave } from "./SoundWave";
import { fetchPartnerSite, submitSiteContact, submitListingInquiry, reformulateMessage, SITE_DEFAULT_COVERS, API, fetchSiteNetwork } from "./api";
import { authApi, useAuth } from "./authApi";
import { ListingCard } from "./ListingCard";
import LeafletMiniMap from "./LeafletMiniMap";
import { MediaBackground, parseVideo } from "./MediaBackground";
import { JoinContent } from "./join/JoinContent";
import { SiteAIChatBubble, SiteAIChatPage } from "./SiteAIChat";
import { WhyOffMarket } from "./WhyOffMarket";
import AcquereurExperience from "./AcquereurExperience";
import { PreFooterCTA } from "./PreFooterCTA";
import JoinModal from "./join/JoinModal";

// Logo réseau SAFTI (affiché sur les sites perso des conseillers dont la société est SAFTI).
// URL ABSOLUE vers le backend : indispensable car les sites sont servis sur des domaines statiques
// (preview.static / production) où le chemin relatif /api/media ne route pas vers l'API.
const SAFTI_NETWORK_LOGO = `${API}/media/off-markette/brand/safti-logo-22537f79.png`;
// Marque SAFTI seule (maison + SAFTI, sans baseline) — la baseline est animée en texte à part.
const SAFTI_MARK = `${API}/media/off-markette/brand/safti-mark-800f0878.png`;

// Marques réseau (URL absolue vers le backend, cf. note ci-dessus sur les domaines statiques).
const NETWORK_LOGOS = {
  safti: SAFTI_MARK,
  iad: `${API}/media/off-markette/brand/iad-mark-white-2da4ba4b.png`,
  capifrance: `${API}/media/off-markette/brand/capifrance-mark-white-0decd5d2.png`,
  commissaire: `${API}/media/off-markette/brand/gcjai-mark-white-16dd22e1.png`,
};
// Image générique (Château de Chantilly, reprise de la page Philosophie) utilisée en fond de hero
// quand le conseiller n'a PAS de photo. Si le conseiller a une photo, c'est la sienne qui prime.
const HERO_FALLBACK = "https://images.unsplash.com/photo-1659526062822-72f35fedff25?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
// Métadonnées par réseau. chip=true → logo posé sur une pastille blanche (marques pensées pour fond clair).
// presets = jeux de 2 couleurs proposés/pré-enregistrés dans l'éditeur (bi-couleur : les 2 couleurs du logo).
export const NETWORK_META = {
  safti:       { primary: "#EB6402", accent: "",        chip: false, alt: "SAFTI" },
  iad:         { primary: "#006090", accent: "",        chip: false, scale: 1.55, alt: "IAD" },
  capifrance:  { primary: "#0090F0", accent: "#5CC0FF", chip: false, scale: 1.2, alt: "Capifrance",
                 presets: [{ label: "Bleu vif", primary: "#0090F0", accent: "#5CC0FF" },
                           { label: "Bleu clair", primary: "#5CC0FF", accent: "#0090F0" }] },
  century21:   { primary: "#A6874E", accent: "#C9B78E", chip: false, alt: "Century 21",
                 brandStyle: "name", label: "Century 21" },
  commissaire: { primary: "#003090", accent: "#D83048", chip: false, scale: 1.5, alt: "Commissaire de justice",
                 presets: [{ label: "Dominante bleue", primary: "#003090", accent: "#D83048" },
                           { label: "Dominante rouge", primary: "#D83048", accent: "#003090" }] },
};
// Détection du réseau : par PROFIL (commissaire) puis par SOCIÉTÉ (iad/capifrance/safti). Miroir de site_builder.network_of.
export function networkOf(m) {
  const comp = (m?.company || "").toLowerCase();
  const role = (m?.account_type || "").trim().toLowerCase();
  if (role === "commissaire de justice") return "commissaire";
  if (comp.includes("iad")) return "iad";
  if (comp.includes("capifrance") || comp.includes("capi france") || comp.includes("capi-france")) return "capifrance";
  if (comp.includes("century") || comp.replace(/[\s-]/g, "") === "c21") return "century21";
  if (comp.includes("safti")) return "safti";
  return "";
}

// Overrides CSS "couleur de marque" (SAFTI orange, IAD bleu…) appliqués sous un scope donné
// (.psite pour le site perso, .netscope pour l'espace acquéreur).
// - Textes & fonds DORÉS #C19A6B → couleur de marque var(--sp) (base + survol séparés proprement)
// - Contours DORÉS → gris légers (jamais orange)
// Le :not([class*="hover:"]) évite de forcer en permanence un état de survol (ex: bouton Partager
// = fond blanc + hover orange : on ne doit PAS le peindre en orange en permanence).
const networkThemeCss = (s) => `
  ${s} [class*="text-[#C19A6B]"]:not([class*="hover:"]){color:var(--sp)!important}
  ${s} [class*="text-[#A88358]"]:not([class*="hover:"]){color:var(--sp)!important}
  ${s} [class*="hover:text-[#C19A6B]"]:hover{color:var(--sp)!important}
  ${s} [class*="bg-[#C19A6B]"]:not([class*="hover:"]):not([class*="bg-[#C19A6B]/"]){background-color:var(--sp)!important}
  ${s} [class*="bg-[#C19A6B]"]:not([class*="hover:"]):not([class*="bg-[#C19A6B]/"]){color:#fff!important}
  ${s} [class*="bg-[#C19A6B]/"]:not([class*="hover:"]){background-color:color-mix(in srgb, var(--sp) 18%, transparent)!important}
  ${s} [class*="hover:bg-[#C19A6B]"]:hover{background-color:var(--sp)!important;color:#fff!important}
  ${s} .btn-accent,${s} .btn-accent2,${s} .bg-accent{color:#fff!important}
  ${s} a[class*="bg-white"]:not([class*="bg-white/"]):not([class*="hover:"]),${s} button[class*="bg-white"]:not([class*="bg-white/"]):not([class*="hover:"]){background-color:#eeece8!important}
  ${s} [class*="hover:bg-[#d1af84]"]:hover,${s} [class*="hover:bg-[#A88358]"]:hover{background-color:var(--sp2)!important}
  ${s} [class*="border-[#C19A6B]"]:not([class*="hover:"]):not([class*="focus:"]){border-color:rgba(255,255,255,0.16)!important}
  ${s} [class*="focus:border-[#C19A6B]"]:focus{border-color:rgba(255,255,255,0.32)!important}
  ${s} [class*="hover:border-[#C19A6B]"]:hover{border-color:rgba(255,255,255,0.28)!important}
`;

// Logo réseau façon OFF MARKETTE : marque + baseline animée mot par mot.
// Taille d'origine ; la baseline disparaît au scroll (header solid) pour ne garder que la marque.
const NetworkLogo = ({ net = "safti", solid = false, footer = false, viewTrigger = false, name = "" }) => {
  const meta = NETWORK_META[net] || NETWORK_META.safti;
  const src = NETWORK_LOGOS[net] || SAFTI_MARK;
  const isName = meta.brandStyle === "name";
  const trigger = viewTrigger
    ? { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-40px" } }
    : { animate: { opacity: 1, y: 0 } };
  const base = viewTrigger ? 0 : 0.4;
  const imgH = footer ? "h-[3.2rem]" : (solid ? "h-[1.44rem]" : "h-[2.59rem]");
  const scale = meta.scale || 1;
  const baseRem = footer ? 3.2 : (solid ? 1.44 : 2.59);
  const hdrShrink = footer ? 1 : 0.9; // -10% de largeur du logo dans le header (demande user)
  const imgStyle = { height: `${(baseRem * scale * hdrShrink).toFixed(2)}rem` };
  const isCompact = solid && !footer; // header scrollé : baseline courte "OFF MARKETTE"
  const baseText = meta.label ? `Mon réseau Off Markette · ${meta.label}` : "Mon réseau Off Markette";
  const words = isCompact ? ["Off", "Markette"] : baseText.split(" ");
  const posCls = footer ? "mt-1.5" : (solid ? "mt-1" : "absolute top-full left-0 right-0 mt-1");
  const sepColor = meta.accent || "var(--sp)"; // bi-couleur : traits en accent (ex. rouge commissaires)
  const baseline = (
    <div data-testid="safti-baseline" className={`${posCls} flex items-center gap-2 ${isCompact ? "pr-0.5" : ""}`}>
      <span className="flex-1 h-px" style={{ backgroundColor: sepColor, opacity: 0.6 }} />
      <p className="whitespace-nowrap text-center text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.16em] text-zinc-200">
        {words.map((w, i) => {
          const isOff = /^(off|markette)$/i.test(w);
          return (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 5 }}
              {...trigger}
              transition={{ delay: base + i * 0.12, duration: 0.5, ease: "easeOut" }}
              className="mr-1"
              style={isOff ? { color: "var(--sp)" } : undefined}
            >
              {w}
            </motion.span>
          );
        })}
      </p>
      {!isCompact && <span className="flex-1 h-px" style={{ backgroundColor: sepColor, opacity: 0.6 }} />}
    </div>
  );
  const img = <img src={src} alt={meta.alt} style={imgStyle} className={`w-auto object-contain transition-all duration-500`} />;
  const nameSize = footer ? "text-2xl" : (isCompact ? "text-base" : "text-xl sm:text-[1.53rem]");
  const mark = isName
    ? <span className={`${nameSize} font-light tracking-[0.2em] uppercase text-white leading-none whitespace-nowrap transition-all duration-500`}>{name || meta.alt}</span>
    : (meta.chip
        ? <span className="inline-flex items-center bg-white rounded-[10px] px-2.5 py-1.5 shadow-sm">{img}</span>
        : img);
  return (
    <div className="relative flex flex-col items-center transition-all duration-500">
      {mark}
      {baseline}
    </div>
  );
};
// Rétro-compat : SAFTI reste l'appel historique.
const SaftiLogo = (props) => <NetworkLogo net="safti" {...props} />;


const DEFAULT_EXPERTISES = [
  { title: "Vente résidentielle", text: "Accompagnement complet des propriétaires, de l'estimation à la signature." },
  { title: "Immobilier de prestige", text: "Une approche confidentielle et sur-mesure pour les biens d'exception." },
  { title: "Accompagnement acquéreur", text: "Recherche ciblée et négociation dans votre intérêt." },
  { title: "Estimation", text: "Une évaluation juste et argumentée, fondée sur le marché local." },
];

const Container = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-6 lg:px-12 ${className}`}>{children}</div>
);

const SectionHead = ({ eyebrow, title, subtitle }) => (
  <motion.div className="space-y-2 mb-12" initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
    <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent">{eyebrow}</span>
    <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">{title}</h2>
    {subtitle && <p className="text-sm sm:text-base font-light text-zinc-400 max-w-xl">{subtitle}</p>}
  </motion.div>
);

const Prose = ({ text }) => (
  <div className="space-y-4">{(text || "").split(/\n{2,}|\n/).filter(Boolean).map((p, i) => <motion.p key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: Math.min(i * 0.08, 0.4), ease: [0.22, 1, 0.36, 1] }} className="text-base font-light text-zinc-300 leading-relaxed">{p}</motion.p>)}</div>
);

// Bannière de couverture pleine largeur (~90% de la hauteur du site national), titre intégré, éditable dans l'admin.
function PageBanner({ image, title, subtitle }) {
  return (
    <section className="relative w-full h-[42vh] min-h-[300px] max-h-[460px] overflow-hidden flex items-end justify-center text-center" data-testid="site-page-banner">
      <motion.div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${image})` }}
        initial={{ scale: 1.14 }} animate={{ scale: 1 }} transition={{ duration: 9, ease: "linear" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.3) 0%, rgba(5,5,5,0.74) 100%)" }} />
      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 pb-12 sm:pb-16">
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="flex flex-col items-center">
          <div className="min-h-[3.5rem] sm:min-h-[4rem] flex items-end justify-center">
            {subtitle && <p className="text-lg sm:text-xl lg:text-2xl font-light text-white leading-snug max-w-2xl">{subtitle}</p>}
          </div>
          {title && <span className="mt-4 text-base sm:text-lg font-normal lowercase tracking-[0.18em] txt-accent inline-flex items-center gap-3"><span className="w-8 h-px bg-accent inline-block" />{title}<span className="w-8 h-px bg-accent inline-block" /></span>}
        </motion.div>
      </div>
    </section>
  );
}

export default function PartnerSite() {
  const { slug, page = "accueil", article } = useParams();
  const [data, setData] = useState(undefined);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isPreview] = useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1");
  const [previewSite, setPreviewSite] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => { fetchPartnerSite(slug).then(setData); }, [slug]);
  useEffect(() => { window.scrollTo(0, 0); }, [page]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    if (!isPreview) return;
    const onMsg = (e) => { if (e.data && e.data.type === "om-preview-site" && e.data.site) setPreviewSite(e.data.site); };
    window.addEventListener("message", onMsg);
    try { window.parent.postMessage({ type: "om-preview-ready" }, "*"); } catch (_) {}
    return () => window.removeEventListener("message", onMsg);
  }, [isPreview]);

  if (data === undefined) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C19A6B] border-t-transparent rounded-full animate-spin" /></div>;
  if (data === null) return <Navigate to="/" replace />;

  const { member: m, listings } = data;
  const site = (isPreview && previewSite) ? previewSite : data.site;
  const primary = site.theme.primary || "#C19A6B";
  const net = networkOf(m);
  const isNetwork = !!net;
  const accent = site.theme.accent || (net && NETWORK_META[net]?.accent) || "";
  const pages = [...site.pages].filter((p) => p.visible).sort((a, b) => a.order - b.order);
  // MARKETTE PLACE : injecte l'onglet « Espace Acquéreur » dans le menu s'il n'existe pas.
  if (!pages.some((p) => p.key === "assistant")) pages.push({ key: "assistant", label: "Espace Acquéreur", visible: true, order: 999 });
  // « Accueil » retiré du menu : le logo cliquable y mène déjà (évite la redondance).
  const navPages = pages.filter((p) => p.key !== "accueil");
  const content = site.content || {};
  const brand = m.company || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.display_name;
  const agentName = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.display_name || brand;
  const nameBrand = NETWORK_META[net]?.brandStyle === "name";
  const brandDisplay = nameBrand ? agentName : brand;
  // Logo de société : réseaux reconnus (SAFTI/IAD/Capifrance/Commissaires) → marque réseau ; les autres gardent le leur.
  const isSafti = net === "safti";
  const waNumber = (m.phone || "").replace(/[^0-9]/g, "").replace(/^0/, "33");
  const linkFor = (k) => (k === "accueil" ? `/${slug}/site` : `/${slug}/site/${k}`) + (isPreview ? "?preview=1" : "");
  const solid = scrolled || page !== "accueil";
  const covers = content.covers || {};
  const bannerFor = (key, title, eyebrow) => ({ image: covers[key]?.image || SITE_DEFAULT_COVERS[key], title: covers[key]?.title || title, subtitle: covers[key]?.subtitle || "", eyebrow });

  return (
    <div className={`psite platform-scope ${isNetwork ? "netbrand" : ""} ${accent ? "bicolor" : ""} ${net ? `net-${net}` : ""} bg-[#050505] text-white min-h-screen`} style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="partner-site">
      <style>{`.psite{--sp:${primary};--sp2:color-mix(in srgb, var(--sp) 78%, #ffffff);--acc:${accent || primary}} .psite .txt-accent{color:var(--sp)} .psite .bg-accent{background-color:var(--sp)} .psite .bd-accent{border-color:rgba(255,255,255,0.16)!important} .psite .btn-accent{background-color:var(--sp);color:#fff} .psite .btn-accent:hover{filter:brightness(1.08)} .psite .btn-accent2{background-color:var(--sp2);color:#fff} .psite .btn-accent2:hover{filter:brightness(1.05)} .psite .hov-accent:hover{color:var(--sp)}
      ${networkThemeCss(".psite")}
      ${accent ? `.psite.bicolor .hov-accent:hover{color:var(--acc)!important} .psite.bicolor .calli{color:var(--acc)!important} .psite.bicolor svg.txt-accent{color:var(--acc)!important} .psite.bicolor .txt-accent2{color:var(--acc)!important} .psite.bicolor .bd-accent2{border-color:var(--acc)!important}` : ""}
      ${isNetwork ? `.psite.netbrand [class*="rounded-none"],.psite.netbrand [class*="rounded-[5px]"],.psite.netbrand [class*="rounded-[4px]"]{border-radius:12px!important}` : ""}
      ${net === "century21" ? `.psite.net-century21{background-color:#141414} .psite.net-century21 footer{background-color:#0e0e0e!important}` : ""}`}</style>
      <Helmet>
        <title>{content.seo?.meta_title || `${brandDisplay} — Immobilier`}</title>
        <meta name="description" content={content.seo?.meta_description || (m.description || "").slice(0, 150) || `${brandDisplay}, votre expert immobilier.`} />
      </Helmet>

      {/* Header — ADN OFF MARKETTE */}
      <header data-testid="site-header" className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${solid ? "bg-[#050505] py-2 border-b border-white/5" : "bg-gradient-to-b from-[#050505]/95 via-[#050505]/85 to-transparent pt-5 pb-16"}`}>
        <Container className={`flex justify-between gap-4 ${isNetwork && !solid ? "items-center lg:items-end" : "items-center"}`}>
          <Link to={linkFor("accueil")} data-testid="site-brand" className="shrink-0 flex items-center gap-3">
            {isNetwork ? <NetworkLogo net={net} name={agentName} solid={solid} /> : (m.logo ? <img src={m.logo} alt={brand} className={`${solid ? "h-9" : "h-[4.5rem]"} w-auto object-contain transition-all duration-500`} /> : <span className={`${solid ? "text-base" : "text-lg"} font-light tracking-[0.2em] uppercase transition-all duration-500`}>{brand}</span>)}
          </Link>
          <nav className="hidden lg:flex items-center gap-x-3 xl:gap-5 lg:ml-4 xl:ml-8">
            {navPages.map((p) => (
              <Link key={p.key} to={linkFor(p.key)} data-testid={`site-nav-${p.key}`} className={`text-[10px] uppercase tracking-[0.08em] xl:tracking-[0.11em] leading-tight text-center whitespace-nowrap transition-colors ${p.key === "assistant" ? "" : "font-bold"} ${page === p.key ? "txt-accent" : "text-white hov-accent"}`}>{p.key === "assistant" ? <>Espace<br /><b className="font-bold">Acquéreur</b></> : p.label}</Link>
            ))}
            {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} data-testid="site-appeler" className="btn-accent rounded-[5px] px-3 xl:px-4 py-2 text-xs font-medium uppercase tracking-wider transition-luxury inline-flex items-center gap-2 shrink-0"><Phone className="w-3.5 h-3.5" /> Appeler</a>}
          </nav>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="site-menu-toggle">{open ? <X /> : <Menu />}</button>
        </Container>
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#050505]/98 px-6 py-5 space-y-3">
            {navPages.map((p) => <Link key={p.key} to={linkFor(p.key)} onClick={() => setOpen(false)} data-testid={`site-navm-${p.key}`} className="block text-xs uppercase tracking-wider text-white">{p.label}</Link>)}
            {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="btn-accent inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider mt-2">Appeler</a>}
          </div>
        )}
      </header>

      {page === "accueil" && !article && <Home m={m} content={content} listings={listings} slug={slug} brand={brandDisplay} heroBrand={brand} partners={site.partners} primary={primary} />}
      {page === "annonces" && <Annonces listings={listings} slug={slug} apporteur={(m.account_type || "").trim().toLowerCase() === "apporteur d'affaires"} banner={bannerFor("annonces", "Nos annonces", "Portefeuille")} />}
      {page === "presentation" && <Presentation m={m} content={content} media={site.media} banner={bannerFor("presentation", `${m.first_name} ${m.last_name}`, "Présentation")} />}
      {page === "expertises" && <Expertises content={content} banner={bannerFor("expertises", "Nos expertises", "Savoir-faire")} />}
      {page === "monreseau" && <MonReseau slug={slug} brand={brandDisplay} banner={bannerFor("monreseau", "Mon réseau", "Réseau collaboratif")} />}
      {page === "partenaires" && <Partenaires partners={site.partners} banner={bannerFor("partenaires", "Partenaires locaux", "Écosystème local")} />}
      {page === "news" && !article && <News news={site.news} slug={slug} banner={bannerFor("news", "News", "Actualités & conseils")} />}
      {article && <NewsArticle news={site.news} article={article} slug={slug} />}
      {page === "reseau" && <ReseauJoin m={m} primary={primary} join={content.join || {}} />}
      {page === "contact" && <Contact m={m} slug={slug} waNumber={waNumber} banner={bannerFor("contact", "Parlons de votre projet", "Contact")} />}
      {page === "assistant" && (
        <div data-testid="site-espace-acquereur">
          <AcquereurExperience slug={slug} brand={brandDisplay} postLoginPath={linkFor("assistant")} />
        </div>
      )}
      {!["accueil", "annonces", "presentation", "expertises", "monreseau", "partenaires", "news", "reseau", "contact", "assistant"].includes(page) && !article && (
        <div className="pt-40 pb-24"><Container><SectionHead eyebrow="Page" title={pages.find((p) => p.key === page)?.label || ""} /><Prose text={content[page]?.text} /></Container></div>
      )}

      {/* Bandeau « Je revendique mon site » — profil auto-provisionné non revendiqué */}
      {!isPreview && m.claimed === false && (
        <ClaimBar agentName={agentName} brandLabel={NETWORK_META[net]?.label || m.company} onOpen={() => setClaimOpen(true)} />
      )}
      {claimOpen && (
        <ClaimModal
          slug={slug} member={m} agentName={agentName}
          onClose={() => setClaimOpen(false)}
          onDone={(res) => { try { setUser(res.user); } catch (_) {} navigate("/mon-compte"); }}
        />
      )}

      {/* Encart générique Achat / Vente (identique à OFF MARKETTE), masqué sur la page Espace Acquéreur */}
      {page !== "assistant" && <PreFooterCTA />}

      {/* Footer — même structure/animations qu'OFF MARKETTE, contenu personnalisé */}
      <footer data-testid="site-footer" className="border-t border-white/10 bg-[#0A0A0C] pt-16 pb-16 mt-8">
        <Container className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <Link to={linkFor("accueil")} className="inline-block mb-4">
              {isNetwork ? <NetworkLogo net={net} name={agentName} footer viewTrigger /> : (m.logo ? <img src={m.logo} alt={brand} className="h-20 w-auto object-contain" /> : <span className="text-lg font-light tracking-[0.2em] uppercase">{brand}</span>)}
            </Link>
            <p className="text-sm font-light text-zinc-500 max-w-xs">{content.accueil?.hero_subtitle || m.sector || "Votre expert immobilier, au plus près de vos projets."}</p>
          </div>
          <div className="space-y-2">
            <h4 className="text-xs uppercase tracking-[0.2em] txt-accent mb-3">Navigation</h4>
            {pages.map((p) => <Link key={p.key} to={linkFor(p.key)} className="block text-sm text-zinc-400 hover:text-white transition-colors">{p.label}</Link>)}
          </div>
          <div className="space-y-2">
            <h4 className="text-xs uppercase tracking-[0.2em] txt-accent mb-3">Contact</h4>
            <p className="text-sm text-zinc-400">{m.first_name} {m.last_name}{m.account_type ? ` · ${m.account_type}` : ""}</p>
            {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="block text-sm text-white hover:text-[#C19A6B]">{m.phone}</a>}
            {m.email && <a href={`mailto:${m.email}`} className="block text-sm text-zinc-400 hover:text-white">{m.email}</a>}
          </div>
        </Container>
        <p className="text-center text-xs text-zinc-600 mt-12">© {new Date().getFullYear()} {brandDisplay} — Membre du réseau <Link to="/" className="txt-accent">OFF MARKETTE</Link></p>
      </footer>
      {!isPreview && page !== "assistant" && <SiteAIChatBubble slug={slug} brand={brandDisplay} />}
    </div>
  );
}

// Bandeau flottant « C'est votre site ? » — visible uniquement sur un profil non revendiqué.
function ClaimBar({ agentName, brandLabel, onOpen }) {
  return (
    <motion.div
      initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-0 left-0 right-0 z-[60]" data-testid="claim-bar">
      <div className="mx-auto max-w-5xl m-3 sm:m-4">
        <div className="rounded-[14px] border border-white/12 bg-[#0b0b0d]/95 backdrop-blur-xl shadow-2xl shadow-black/50 px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <span className="mt-0.5 shrink-0 w-9 h-9 rounded-full bg-accent/15 border border-[color:var(--sp)]/40 flex items-center justify-center"><ShieldCheck className="w-4.5 h-4.5 txt-accent" /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white leading-tight">Ce site a été préparé pour <span className="txt-accent">{agentName}</span>{brandLabel ? ` · ${brandLabel}` : ""}.</p>
              <p className="text-[12px] text-zinc-400 leading-tight mt-0.5">C'est vous ? Revendiquez-le en 1 minute pour prendre la main : éditez votre fiche et gérez vos annonces.</p>
            </div>
          </div>
          <button onClick={onOpen} data-testid="claim-open-btn"
            className="btn-accent shrink-0 rounded-[8px] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-luxury hover:brightness-110">
            <KeyRound className="w-4 h-4" /> Je revendique mon site
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Modale de revendication instantanée : e-mail (pré-rempli) + mot de passe → prise en main immédiate.
function ClaimModal({ slug, member, agentName, onClose, onDone }) {
  const [email, setEmail] = useState(member.email || "");
  const [phone, setPhone] = useState(member.phone || "");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) { setErr("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (pwd !== pwd2) { setErr("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      const res = await authApi.claimSite(slug, { email: email.trim(), password: pwd, phone: phone.trim() });
      onDone(res);
    } catch (e2) {
      setErr(e2.message || "La revendication a échoué. Réessayez.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="claim-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full sm:max-w-md bg-[#0b0b0d] border border-white/12 rounded-t-[18px] sm:rounded-[16px] shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-white/8">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] txt-accent"><KeyRound className="w-4 h-4" /> Revendiquer mon site</span>
            <button onClick={onClose} data-testid="claim-close-btn" className="text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <h3 className="mt-3 text-xl font-light text-white leading-snug">Bienvenue, <span className="txt-accent">{agentName}</span>.</h3>
          <p className="mt-1 text-[13px] text-zinc-400">Créez votre accès : vous prenez la main <b className="text-zinc-200">immédiatement</b>, sans validation. Vous pourrez éditer votre fiche et gérer vos annonces.</p>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-3.5">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">E-mail professionnel</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="claim-email"
              className="w-full bg-white/5 border border-white/15 rounded-[8px] px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--sp)] transition-colors" placeholder="prenom.nom@century21.fr" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Téléphone (optionnel)</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="claim-phone"
              className="w-full bg-white/5 border border-white/15 rounded-[8px] px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--sp)] transition-colors" placeholder="+33 6 …" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Mot de passe (8 caractères min.)</label>
            <div className="relative">
              <input type={showPwd ? "text" : "password"} required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="claim-password"
                className="w-full bg-white/5 border border-white/15 rounded-[8px] px-3 py-2.5 pr-10 text-sm text-white outline-none focus:border-[color:var(--sp)] transition-colors" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200" tabIndex={-1}>{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Confirmer le mot de passe</label>
            <input type={showPwd ? "text" : "password"} required value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="claim-password2"
              className="w-full bg-white/5 border border-white/15 rounded-[8px] px-3 py-2.5 text-sm text-white outline-none focus:border-[color:var(--sp)] transition-colors" placeholder="••••••••" />
          </div>
          {err && <p className="text-[13px] text-red-400" data-testid="claim-error">{err}</p>}
          <button type="submit" disabled={loading} data-testid="claim-submit"
            className="btn-accent w-full rounded-[8px] py-3 text-sm font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-luxury hover:brightness-110 disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Activation…</> : <>Prendre la main <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[11px] text-zinc-600 text-center leading-snug">En revendiquant ce site, vous devenez responsable de son contenu au sein du réseau OFF MARKETTE.</p>
        </form>
      </motion.div>
    </div>
  );
}


function Home({ m, content, listings, slug, brand, heroBrand, partners = [], primary = "#C19A6B" }) {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const hasVideo = !!parseVideo(m.hero_video);
  const ownPhotos = (m.gallery && m.gallery.length ? m.gallery : (m.photo ? [m.photo] : []));
  // Si le conseiller n'a AUCUNE photo → fond de hero générique (Château de Chantilly) pour un rendu soigné.
  const photos = ownPhotos.length ? ownPhotos : [HERO_FALLBACK];
  // Portrait conseiller → cadrage haut ; fallback paysage (château) → centré verticalement.
  const heroPos = ownPhotos.length ? "center 10%" : "center 42%";
  useEffect(() => {
    if (hasVideo || photos.length < 2) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % photos.length), 6000);
    return () => clearInterval(t);
  }, [hasVideo, photos.length]);
  const hero = content.accueil || {};
  const featured = listings.slice(0, 3);
  const scrollToReseau = () => document.getElementById("site-reseau-partenaires")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      {/* Hero plein écran — ADN OFF MARKETTE */}
      <section data-testid="site-hero" className="relative min-h-screen flex items-end justify-start overflow-hidden pb-12 sm:pb-20 pt-28">
        <div className="absolute inset-0 z-0">
          {hasVideo ? <MediaBackground videoUrl={m.hero_video} cover muted={muted} />
            : photos.length ? (
              <AnimatePresence mode="sync">
                <motion.div key={idx} className="absolute inset-0 bg-cover" style={{ backgroundImage: `url(${photos[idx]})`, backgroundPosition: heroPos }}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: "easeInOut" }} />
              </AnimatePresence>
            ) : <div className="absolute inset-0 bg-accent" style={{ opacity: 0.12 }} />}
          {photos.length > 1 && (
            <div className="absolute inset-0 pointer-events-none z-[2]" aria-hidden="true">
              {[["10%","8%",9,0],["18%","30%",7,0.06],["12%","62%",10,0.1],["22%","82%",6,0.04],["30%","18%",8,0.14],["34%","50%",7,0.08],["40%","74%",11,0.12],["46%","10%",6,0.18],["52%","38%",9,0.05],["56%","88%",7,0.16],["62%","24%",8,0.1],["66%","60%",6,0.2],["72%","44%",10,0.07],["76%","14%",7,0.13],["80%","78%",9,0.09],["84%","34%",6,0.17],["88%","56%",8,0.11],["26%","66%",7,0.15],["48%","62%",6,0.19],["70%","90%",9,0.06],["14%","46%",7,0.12],["58%","6%",8,0.03]].map((p, i) => (
                <motion.svg key={`${idx}-${i}`} viewBox="0 0 24 24" fill="#F0D6AB"
                  style={{ position: "absolute", top: p[0], left: p[1], width: p[2], height: p[2], filter: "drop-shadow(0 0 6px rgba(193,154,107,1))" }}
                  initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: [0, 1, 1, 0], scale: [0.3, 1.15, 1, 0.7] }}
                  transition={{ duration: 2.1, ease: "easeOut", delay: p[3], times: [0, 0.18, 0.4, 1] }}>
                  <path d="M12 0l2.4 8.4L23 12l-8.6 3.6L12 24l-2.4-8.4L1 12l8.6-3.6z" />
                </motion.svg>
              ))}
            </div>
          )}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.32) 42%, rgba(5,5,5,0) 72%)" }} />
        </div>

        {!hasVideo && photos.length > 1 && (
          <>
            <button onClick={() => setIdx((p) => (p - 1 + photos.length) % photos.length)} className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 p-3 border border-white/20 text-white/80 hover:bg-white hover:text-black transition-luxury"><ArrowLeft className="w-5 h-5" /></button>
            <button onClick={() => setIdx((p) => (p + 1) % photos.length)} className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 p-3 border border-white/20 text-white/80 hover:bg-white hover:text-black transition-luxury"><ArrowRight className="w-5 h-5" /></button>
          </>
        )}

        <Container className="w-full relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-8 space-y-4">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent">{m.account_type || "Conseiller immobilier"}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-white">{hero.hero_title || heroBrand || brand}</h1>
            {m.sector && <p className="text-sm sm:text-base font-light tracking-[0.15em] uppercase txt-accent" data-testid="hero-territory">Immobilier · {m.sector}</p>}
            <p className="text-base sm:text-xl font-light text-zinc-300 max-w-2xl leading-relaxed">{hero.hero_subtitle || m.sector || "Les plus belles opportunités immobilières, souvent avant tout le monde."}</p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to={`/${slug}/site/annonces`} data-testid="site-cta-annonces" className="btn-accent px-8 py-4 font-medium tracking-wide transition-luxury rounded-[5px] text-sm inline-flex items-center gap-2 group">
                <HomeIcon className="w-4 h-4" /> MES ANNONCES <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to={`/${slug}/site/monreseau`} data-testid="site-cta-reseau" className="bg-white/5 backdrop-blur-sm border border-white/30 text-white hover:bg-white hover:text-[#050505] px-8 py-4 font-medium transition-luxury rounded-[5px] text-sm inline-flex items-center gap-2 group">
                <Handshake className="w-4 h-4" /> MON RÉSEAU <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              {hasVideo && (
                <button type="button" onClick={() => setMuted((x) => !x)} className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 text-white hover:bg-white/20 px-5 py-4 font-medium transition-luxury rounded-[5px] text-sm">
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}<span className="hidden sm:inline">{muted ? "Activer le son" : "Couper le son"}</span>
                </button>
              )}
            </div>
          </motion.div>
          <div className="lg:col-span-4 lg:text-right">
            <div className="inline-block bg-white/5 backdrop-blur-md border border-white/10 p-6 sm:p-8 rounded-[5px] text-left lg:text-right w-full lg:w-auto">
              <span className="text-xs font-medium txt-accent uppercase tracking-widest block mb-1">{m.company || "Votre conseiller"}</span>
              <span className="text-3xl sm:text-4xl font-light text-white tracking-tight block mb-2">{m.first_name} {m.last_name}</span>
              <div className="border-t border-white/10 pt-3 space-y-1">
                {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="block text-base sm:text-lg font-normal text-zinc-200 hover:text-white">{m.phone}</a>}
                {(m.sector || m.city) && <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest">{m.sector || m.city}</p>}
              </div>
            </div>
          </div>
        </Container>

        {!hasVideo && photos.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {photos.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-accent" : "w-3 bg-white/40 hover:bg-white/70"}`} />)}
          </div>
        )}
      </section>

      {/* Biens à la une */}
      {featured.length > 0 && (
        <section data-testid="site-featured" className="sp-sec sp-sec-gray py-24 sm:py-32 border-y">
          <Container>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div className="space-y-2">
                <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent">Sélection</span>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Biens à la une</h2>
              </div>
              <Link to={`/${slug}/site/annonces`} className="text-xs uppercase tracking-[0.18em] txt-accent hover:opacity-80 inline-flex items-center gap-2">Tout voir <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{featured.map((l, i) => (
              <motion.div key={l.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}><ListingCard listing={l} index={i} /></motion.div>
            ))}</div>
          </Container>
        </section>
      )}

      {/* Mon réseau — partenaires locaux & réseau collaboratif */}
      <section id="site-reseau-partenaires" data-testid="site-reseau-section" className="sp-sec sp-sec-white py-24 sm:py-32 border-b scroll-mt-24">
        <Container>
          <SectionHead eyebrow="Mon réseau" title="Un réseau au service de vos projets" subtitle="Des partenaires de confiance et un réseau collaboratif national pour vous accompagner à chaque étape." />
          {partners.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="site-home-partners">
              {partners.slice(0, 4).map((p, i) => (
                <motion.div key={p.id || i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 4) * 0.08 }} className="group border border-white/10 p-6 hover:border-white/25 hover:bg-white/[0.02] transition-luxury flex flex-col items-start">
                  {p.logo ? <img src={p.logo} alt={p.name} className="w-14 h-14 object-contain bg-white/5 p-1 mb-4 transition-transform duration-500 group-hover:scale-105" /> : <div className="w-14 h-14 bg-white/5 flex items-center justify-center txt-accent mb-4"><Building2 className="w-6 h-6" /></div>}
                  <h3 className="text-base font-light text-white">{p.name}</h3>
                  {p.category && <span className="text-[10px] uppercase tracking-widest txt-accent mt-1">{p.category}</span>}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6" data-testid="site-home-network-teaser">
              {[{ icon: Handshake, t: "Partenaires locaux", d: "Notaires, courtiers, artisans et experts sélectionnés pour la réussite de votre projet." },
                { icon: Users, t: "Réseau national", d: "Un réseau de conseillers partageant leurs mandats pour élargir vos opportunités." },
                { icon: Award, t: "Accompagnement premium", d: "Un interlocuteur unique et un service sur-mesure du premier contact à la signature." }].map((c, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: i * 0.1 }} className="group border border-white/10 p-8 hover:border-white/25 hover:bg-white/[0.02] transition-luxury">
                  <c.icon className="w-7 h-7 mb-5 txt-accent transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="text-lg font-light text-white mb-2">{c.t}</h3>
                  <p className="text-sm text-zinc-400 font-light leading-relaxed">{c.d}</p>
                </motion.div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to={`/${slug}/site/partenaires`} className="btn-accent px-7 py-3.5 text-sm font-medium uppercase tracking-wider transition-luxury inline-flex items-center gap-2 group">Nos partenaires <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></Link>
            <Link to={`/${slug}/site/reseau`} className="border bd-accent txt-accent px-7 py-3.5 text-sm font-medium uppercase tracking-wider hover:bg-white/5 transition-luxury inline-flex items-center gap-2">Rejoindre le réseau</Link>
          </div>
        </Container>
      </section>

      {/* Pourquoi rejoindre OFF MARKETTE ? — concept off market (gains + processus) */}
      <WhyOffMarket accent={primary} joinTo={`/${slug}/site/reseau`} joinLabel="Rejoindre le réseau" name={m.first_name || ""} />

      {/* À propos */}
      {m.description && (
        <section className="sp-sec sp-sec-gray py-24 sm:py-32">
          <Container className="max-w-3xl text-center">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent">À propos</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white mt-2 mb-8">{brand}</h2>
            <Prose text={content.presentation?.text || m.description} />
          </Container>
        </section>
      )}
    </>
  );
}

function InquiryModal({ slug, listing, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: `Bonjour, je souhaite être mis en relation à propos de « ${listing?.title || ""} ».` });
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try { await submitListingInquiry(slug, { listing_slug: listing.slug, ...f }); setState("sent"); }
    catch (_) { setState("error"); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="site-inquiry-modal">
      <div className="bg-[#0A0A0C] border border-white/10 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-light text-white">Être mis en relation</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {state === "sent" ? (
          <div className="text-center py-6" data-testid="site-inquiry-success">
            <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-zinc-300">Votre demande a bien été transmise. Le conseiller vous recontactera rapidement.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-zinc-500">À propos de <span className="text-[#C19A6B]">{listing?.title}</span></p>
            <input required placeholder="Nom" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="inquiry-name" className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
            <input required type="email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="inquiry-email" className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
            <input placeholder="Téléphone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="inquiry-phone" className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
            <textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="inquiry-message" className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
            {state === "error" && <p className="text-xs text-red-400">Envoi impossible, réessayez.</p>}
            <button type="submit" disabled={state === "sending"} data-testid="inquiry-submit" className="w-full btn-accent font-medium uppercase tracking-wider text-sm py-3 transition-colors">{state === "sending" ? "Envoi…" : "Envoyer ma demande"}</button>
          </form>
        )}
      </div>
    </div>
  );
}

const APPORTEUR_ROLE_ITEMS = [
  "Trouver le professionnel qui possède le bien correspondant parfaitement à votre recherche.",
  "Identifier l'acquéreur idéal pour un vendeur.",
  "Vous mettre en relation avec les partenaires du réseau pour vos projets travaux (artisans, architectes, rénovation…).",
  "Vous accompagner dans vos recherches de financement grâce aux partenaires courtiers.",
  "Faciliter chaque étape de votre projet grâce au réseau OFF MARKETTE.",
];

// Bloc « Mon rôle » — affiché uniquement sur la page annonces des comptes Apporteur d'affaires.
function ApporteurRoleBlock() {
  return (
    <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative border border-white/10 bg-white/[0.02] rounded-[5px] p-7 sm:p-10 mb-14 overflow-hidden" data-testid="apporteur-role-block">
      <span aria-hidden="true" className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none bg-accent" style={{ opacity: 0.12 }} />
      <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-4">
          <span className="text-xs font-semibold tracking-[0.28em] uppercase txt-accent inline-flex items-center gap-2"><Handshake className="w-4 h-4" /> Apporteur d'affaires</span>
          <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mt-4 leading-snug">Mon rôle</h2>
          <p className="text-sm sm:text-base font-light text-zinc-300 mt-4 leading-relaxed">
            Je ne suis pas là pour vendre directement un bien. Ma mission est de vous accompagner en créant les bonnes connexions entre <span className="text-white">vendeurs, acquéreurs et professionnels du réseau</span>.
          </p>
        </div>
        <div className="lg:col-span-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400 mb-5">Je peux vous aider à :</p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {APPORTEUR_ROLE_ITEMS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-3.5">
                <span className="mt-1 w-3.5 h-3.5 shrink-0 rounded-[3px] bg-accent" style={{ boxShadow: "0 0 12px rgba(193,154,107,0.5)" }} />
                <p className="text-sm font-light text-zinc-300 leading-relaxed">{t}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Annonces({ listings, slug, banner, apporteur = false }) {
  const [inquiry, setInquiry] = useState(null);
  const ctaLabel = apporteur ? "Être mis en relation" : "Contacter le vendeur";
  return (
    <>
      <PageBanner {...banner} subtitle={banner.subtitle || `${listings.length} bien${listings.length > 1 ? "s" : ""} disponible${listings.length > 1 ? "s" : ""} en exclusivité.`} />
      <div className="sp-sec sp-sec-white py-20 sm:py-24">
      <Container>
        {apporteur && <ApporteurRoleBlock />}
        {listings.length === 0 ? <p className="text-zinc-500">Aucun bien publié pour le moment.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="site-listings">
            {listings.map((l, i) => (
              <motion.div key={l.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }} className="flex flex-col">
                <ListingCard listing={l} index={i} />
                <button onClick={() => setInquiry(l)} data-testid={`site-inquiry-${l.slug}`} className="mt-2 inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.18em] border border-[#C19A6B]/40 txt-accent hover:bg-[#C19A6B]/10 py-2.5 transition-colors rounded-[5px]">{ctaLabel}</button>
              </motion.div>
            ))}
          </div>
        )}
      </Container>
      {inquiry && <InquiryModal slug={slug} listing={inquiry} onClose={() => setInquiry(null)} />}
      </div>
    </>
  );
}

function Presentation({ m, content, media, banner }) {
  const img = (m.gallery && m.gallery[0]) || m.photo;
  const img2 = (m.gallery && m.gallery[1]) || null;
  const stats = [
    m.account_type && { icon: Award, label: "Profil", value: m.account_type },
    m.company && { icon: Building2, label: "Enseigne", value: m.company },
    (m.sector || m.city) && { icon: MapPin, label: "Secteur", value: m.sector || m.city },
  ].filter(Boolean);
  return (
    <>
      <PageBanner {...banner} eyebrow="Présentation" subtitle={banner.subtitle || m.account_type || "Votre conseiller immobilier"} />
      <div className="sp-sec sp-sec-white py-20 sm:py-28">
        <Container>
          {/* Intro : texte aéré + portrait */}
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="lg:col-span-7 space-y-8">
              <div className="space-y-3">
                <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent inline-flex items-center gap-2"><span className="w-6 h-px bg-accent" /> Qui suis-je</span>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">Faisons connaissance</h2>
              </div>
              <div className="text-lg leading-loose"><Prose text={content.presentation?.text || m.description || "Présentation à venir."} /></div>
              {stats.length > 0 && (
                <div className="grid sm:grid-cols-3 gap-4 pt-4">
                  {stats.map((s, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="border border-white/10 p-5 hover:border-white/25 transition-luxury">
                      <s.icon className="w-5 h-5 txt-accent mb-3" />
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</p>
                      <p className="text-sm text-white font-light mt-1">{s.value}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: 0.12 }} className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
              {img && <div className="overflow-hidden border border-white/10 group"><img src={img} alt={`${m.first_name} ${m.last_name}`} className="w-full aspect-[4/5] object-cover transition-transform duration-1000 group-hover:scale-105" /></div>}
              {m.vision && (
                <div className="relative border border-white/10 bg-white/[0.02] p-7">
                  <Sparkles className="w-6 h-6 txt-accent mb-3" />
                  <p className="text-base font-light italic text-zinc-200 leading-relaxed">« {m.vision} »</p>
                  <span className="block mt-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500">Ma vision du métier</span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Secteur */}
          {(content.secteur?.text || m.sector) && (
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="mt-24 grid lg:grid-cols-12 gap-12 items-center border-t border-white/10 pt-16" data-testid="site-secteur">
              {img2 && <div className="lg:col-span-5 overflow-hidden border border-white/10 group order-2 lg:order-1"><img src={img2} alt="secteur" className="w-full aspect-[4/3] object-cover transition-transform duration-1000 group-hover:scale-105" /></div>}
              <div className={img2 ? "lg:col-span-7 order-1 lg:order-2" : "lg:col-span-12"}>
                <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Secteur</span>
                <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-white mt-3 mb-5">{m.sector || m.city || "Mon secteur"}</h3>
                <Prose text={content.secteur?.text || `${m.sector || m.city} : un secteur recherché aux multiples atouts immobiliers. Contactez-moi pour une analyse détaillée des prix et des meilleures opportunités d'investissement.`} />
              </div>
            </motion.div>
          )}
          <MediaGallery media={media} />
        </Container>
      </div>
    </>
  );
}

function embedSrc(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function MediaGallery({ media }) {
  const photos = media?.photos || [];
  const videos = media?.videos || [];
  if (!photos.length && !videos.length) return null;
  return (
    <div className="mt-20 space-y-12" data-testid="site-media">
      {photos.length > 0 && (
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent">En images</span>
          <h3 className="text-2xl font-light tracking-tight text-white mt-1 mb-6">Galerie & réalisations</h3>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5">
            {photos.map((src, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }} className="group relative mb-4 sm:mb-5 break-inside-avoid overflow-hidden bg-black">
                <img src={src} alt="" loading="lazy" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" />
              </motion.div>
            ))}
          </div>
        </div>
      )}
      {videos.length > 0 && (
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase txt-accent">Vidéos</span>
          <h3 className="text-2xl font-light tracking-tight text-white mt-1 mb-6">En vidéo</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {videos.map((v, i) => { const src = embedSrc(v); return (
              <div key={i} className="aspect-video border border-white/10 overflow-hidden bg-black">
                {src ? <iframe title={`video-${i}`} src={src} className="w-full h-full" allowFullScreen frameBorder="0" />
                  : <video src={v} controls className="w-full h-full object-cover" />}
              </div>
            ); })}
          </div>
        </div>
      )}
    </div>
  );
}

const PARTNER_CATS = ["Courtier", "Notaire", "Diagnostiqueur", "Architecte", "Décorateur", "Artisan", "Déménageur", "Photographe", "Home stager", "Autre"];

const NETWORK_FILTERS = [
  { key: "all", label: "Tous", match: null },
  { key: "agent", label: "Agents immobiliers", match: ["agent"] },
  { key: "conseiller", label: "Conseillers immobiliers", match: ["conseiller", "mandataire"] },
  { key: "marchand", label: "Marchands de biens", match: ["marchand"] },
  { key: "apporteur", label: "Apporteurs d'affaires", match: ["apporteur"] },
];

// Page « Mon Réseau » — même design premium que « Notre Réseau » national, limité au réseau personnel du membre.
function MonReseau({ slug, brand, banner }) {
  const [members, setMembers] = useState(null);
  const [flt, setFlt] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => { fetchSiteNetwork(slug).then((d) => setMembers((d && d.members) || [])); }, [slug]);

  const active = NETWORK_FILTERS.find((f) => f.key === flt) || NETWORK_FILTERS[0];
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (members || []).filter((m) => {
      if (active.match && !active.match.some((k) => (m.account_type || "").toLowerCase().includes(k))) return false;
      if (needle) {
        const hay = `${m.display_name || ""} ${m.company || ""} ${m.city || ""} ${m.account_type || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [members, active, q]);

  return (
    <>
      <PageBanner {...banner} subtitle={banner.subtitle || "Les professionnels de l'immobilier avec qui je collabore pour multiplier vos opportunités."} />
      <div className="bg-[#F4F5F7] pt-0 pb-20 sm:pb-24" data-testid="site-monreseau">
        <Container>
          {/* Barre recherche + filtres sur une seule ligne, à cheval sur le bas de la photo (comme le national) */}
          <div className="max-w-5xl mx-auto -mt-[30px] sm:-mt-[38px] mb-10 relative z-40 bg-[#0d0d10] backdrop-blur-xl border border-white/10 rounded-[5px] p-2.5 sm:p-3 shadow-2xl" data-testid="monreseau-filterbar">
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <div className="relative md:w-60 shrink-0">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C19A6B]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, ville…" data-testid="monreseau-search"
                  className="w-full bg-white/[0.06] border border-white/15 rounded-full pl-10 pr-4 py-2 text-[13px] text-white placeholder-zinc-400 focus:outline-none focus:border-[#C19A6B] transition-colors" />
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-1 flex-1" data-testid="monreseau-filters">
                {NETWORK_FILTERS.map((f) => {
                  const on = f.key === flt;
                  return (
                    <button key={f.key} onClick={() => setFlt(f.key)} data-testid={`monreseau-filter-${f.key}`}
                      className={`relative px-2.5 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide transition-all duration-300 ${on ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                      {on && <motion.span layoutId="monreseau-pill" className="absolute inset-0 rounded-full bg-accent" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                      <span className="relative z-10">{f.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {members === null ? (
            <p className="text-center text-zinc-400 py-16">Chargement…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="monreseau-empty">
              <Users className="w-9 h-9 mx-auto mb-4 txt-accent" />
              <p className="text-base font-light text-zinc-600 max-w-md mx-auto">
                {(members || []).length === 0
                  ? "Le réseau collaboratif se construit — de nouveaux professionnels rejoindront bientôt ce réseau."
                  : "Aucun membre ne correspond à ce filtre."}
              </p>
              <Link to={`/${slug}/site/reseau`} className="mt-6 inline-flex items-center gap-2 btn-accent px-6 py-3 rounded-[5px] text-xs font-semibold uppercase tracking-wider transition-luxury">
                <UserPlus className="w-4 h-4" /> Rejoindre le réseau
              </Link>
            </div>
          ) : (
            <>
              <p className="text-center calli text-3xl sm:text-4xl text-[#C19A6B] leading-none mb-8" data-testid="monreseau-count">{filtered.length} professionnel{filtered.length > 1 ? "s" : ""} dans mon réseau</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="monreseau-grid">
                {filtered.map((m, i) => (
                  <motion.div key={m.member_id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} whileHover={{ y: -4 }}
                    className="h-full bg-white border border-zinc-200 rounded-[5px] p-4 hover:border-[color:var(--sp)] hover:shadow-lg hover:shadow-black/5 transition-all flex flex-col" data-testid={`monreseau-card-${m.slug}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border bd-accent bg-zinc-100 flex items-center justify-center shrink-0">
                        {m.photo ? <img src={m.photo} alt={m.display_name} className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 txt-accent" />}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-light text-zinc-900 truncate flex items-center gap-1.5">
                          <span className="truncate">{m.display_name}</span>
                          {(m.verified || m.is_founder) && <BadgeCheck className="w-4 h-4 shrink-0 txt-accent" data-testid={`monreseau-verified-${m.slug}`} />}
                        </h3>
                        {m.company && <p className="text-[11px] txt-accent uppercase tracking-wider truncate">{m.company}</p>}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="space-y-1 text-[13px] text-zinc-600 min-w-0">
                        {m.account_type && <p className="flex items-center gap-1.5 truncate"><Award className="w-3.5 h-3.5 txt-accent shrink-0" /> <span className="truncate">{m.account_type}</span></p>}
                        {(m.city || m.sector) && <p className="flex items-center gap-1.5 truncate"><MapPin className="w-3.5 h-3.5 txt-accent shrink-0" /> <span className="truncate">{m.city || m.sector}</span></p>}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <a href={`/${m.slug}`} className="group inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider txt-accent hover:gap-1.5 transition-all">Voir le profil <ArrowRight className="w-3 h-3" /></a>
                        {m.site_online && <a href={`/${m.slug}/site`} target="_blank" rel="noopener noreferrer" data-testid={`monreseau-site-${m.slug}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-accent hover:opacity-90 px-2.5 py-1 rounded-[5px] transition-opacity"><ExternalLink className="w-3 h-3" /> Voir son site</a>}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </Container>
      </div>
    </>
  );
}

function Partenaires({ partners = [], banner }) {
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState("all");
  const cats = React.useMemo(() => {
    const set = [];
    (partners || []).forEach((p) => { const c = (p.category || "").trim(); if (c && !set.includes(c)) set.push(c); });
    return set;
  }, [partners]);
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (partners || []).filter((p) => {
      if (flt !== "all" && (p.category || "") !== flt) return false;
      if (needle) {
        const hay = `${p.name || ""} ${p.category || ""} ${p.description || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [partners, q, flt]);
  const FILTERS = [{ key: "all", label: "Tous" }, ...cats.map((c) => ({ key: c, label: c }))];
  return (
    <>
      <PageBanner {...banner} subtitle={banner.subtitle || "Des professionnels de confiance pour vous accompagner à chaque étape de votre projet."} />
      <div className="bg-[#F4F5F7] pt-0 pb-20 sm:pb-24" data-testid="site-partenaires">
        <Container>
          {/* Barre recherche + filtres — identique à « Mon Réseau », à cheval sur le bas de la photo */}
          <div className="max-w-5xl mx-auto -mt-[30px] sm:-mt-[38px] mb-10 relative z-40 bg-[#0d0d10] backdrop-blur-xl border border-white/10 rounded-[5px] p-2.5 sm:p-3 shadow-2xl" data-testid="partenaires-filterbar">
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <div className="relative md:w-60 shrink-0">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C19A6B]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, catégorie…" data-testid="partenaires-search"
                  className="w-full bg-white/[0.06] border border-white/15 rounded-full pl-10 pr-4 py-2 text-[13px] text-white placeholder-zinc-400 focus:outline-none focus:border-[#C19A6B] transition-colors" />
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-1 flex-1" data-testid="partenaires-filters">
                {FILTERS.map((f) => {
                  const on = f.key === flt;
                  return (
                    <button key={f.key} onClick={() => setFlt(f.key)} data-testid={`partenaires-filter-${f.key}`}
                      className={`relative px-2.5 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide transition-all duration-300 ${on ? "text-white" : "text-zinc-400 hover:text-white"}`}>
                      {on && <motion.span layoutId="partenaires-pill" className="absolute inset-0 rounded-full bg-accent" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                      <span className="relative z-10">{f.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {partners.length === 0 ? (
            <p className="text-center text-zinc-500 py-10">Réseau de partenaires bientôt disponible.</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-zinc-500 py-10">Aucun partenaire ne correspond à ce filtre.</p>
          ) : (
            <>
              <p className="text-center calli text-3xl sm:text-4xl text-[#C19A6B] leading-none mb-8" data-testid="site-partners-count">{filtered.length} professionnel{filtered.length > 1 ? "s" : ""}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="site-partners">
                {filtered.map((p, i) => (
                  <motion.div key={p.id || i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} whileHover={{ y: -6 }}
                    className="h-full bg-white border border-zinc-200 rounded-[5px] p-6 hover:border-[color:var(--sp)] hover:shadow-xl hover:shadow-black/5 transition-all flex flex-col" data-testid={`partenaire-card-${i}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden border bd-accent bg-zinc-100 flex items-center justify-center shrink-0">
                        {p.logo ? <img src={p.logo} alt={p.name} className="w-full h-full object-cover" /> : <Building2 className="w-7 h-7 txt-accent" />}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-light text-zinc-900 truncate">{p.name}</h3>
                        {p.category && <p className="text-xs txt-accent uppercase tracking-wider truncate">{p.category}</p>}
                      </div>
                    </div>
                    {p.description && <p className="mt-4 text-sm text-zinc-600 font-light leading-relaxed flex-1">{p.description}</p>}
                    <div className="mt-5 flex flex-wrap gap-3 text-sm text-zinc-600">
                      {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 hover:txt-accent transition-colors"><Phone className="w-3.5 h-3.5 txt-accent" /> {p.phone}</a>}
                      {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:txt-accent transition-colors"><Globe className="w-3.5 h-3.5 txt-accent" /> Site</a>}
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </Container>
      </div>
    </>
  );
}

const EXPERTISE_ICON_SET = [Award, Handshake, Sparkles, HomeIcon, Building2, Users];
const EXPERTISE_IMGS = [
  "https://images.unsplash.com/photo-1626178793926-22b28830aa30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1706164971302-e30c0640cc3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1780191751680-cdd89eef7b27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1769063238167-d00e112147c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1758523671285-9ff3f4e0ff38?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1521791055366-0d553872125f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
];

function Expertises({ content, banner }) {
  const items = content.expertises?.items?.length ? content.expertises.items : DEFAULT_EXPERTISES;
  return (
    <>
      <PageBanner {...banner} subtitle={banner.subtitle || "Des compétences pointues au service de vos projets immobiliers."} />
      <div className="sp-sec sp-sec-white py-20 sm:py-28">
      <Container className="space-y-10">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }} className="max-w-2xl">
          <span className="calli text-3xl sm:text-4xl txt-accent">Ce que je fais</span>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 text-zinc-900">De l'estimation à la signature.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="site-expertises">
          {items.map((e, i) => {
            const Ic = EXPERTISE_ICON_SET[i % EXPERTISE_ICON_SET.length];
            const img = e.img || EXPERTISE_IMGS[i % EXPERTISE_IMGS.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }} data-testid={`site-expertise-${i}`}
                className="group relative overflow-hidden bg-zinc-100 border border-zinc-200 rounded-[5px] p-8 hover:border-[#C19A6B]/50 hover:shadow-xl hover:shadow-black/5 transition-all">
                <div className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out" style={{ backgroundImage: `url(${img})` }} />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(to top, rgba(5,5,5,0.92), rgba(5,5,5,0.72))" }} />
                <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                  <Ic className="w-7 h-7 txt-accent transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="text-lg font-semibold text-zinc-900 group-hover:text-white transition-colors">{e.title}</h3>
                  <p className="text-sm font-light text-zinc-600 group-hover:text-white leading-relaxed transition-colors">{e.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Container>
      </div>
    </>
  );
}

function News({ news = [], slug, banner }) {
  const list = [...news].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return (
    <>
      <PageBanner {...banner} subtitle={banner.subtitle || "Analyses de marché, conseils et actualités immobilières de mon secteur."} />
      <div className="bg-[#F4F5F7] py-20 sm:py-24" data-testid="site-news-page">
      <Container>
        {list.length === 0 ? <p className="text-zinc-500">Aucun article pour le moment.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="site-news">
            {list.map((a, i) => (
              <motion.div key={a.id || i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}>
                <Link to={`/${slug}/site/news/${a.slug}`} className="group block bg-white border border-zinc-200 rounded-[5px] overflow-hidden hover:border-[color:var(--sp)] hover:shadow-xl hover:shadow-black/5 transition-all h-full">
                  <div className="aspect-[16/10] overflow-hidden bg-zinc-100">{a.cover ? <img src={a.cover} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full bg-accent" style={{ opacity: 0.12 }} />}</div>
                  <div className="p-5 bg-white">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest mb-2"><span className="txt-accent">{a.tag}</span><span className="text-zinc-400">{a.date}</span></div>
                    <h3 className="text-lg font-light text-zinc-900 leading-snug">{a.title}</h3>
                    {a.excerpt && <p className="text-sm text-zinc-500 font-light mt-2 line-clamp-2">{a.excerpt}</p>}
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider txt-accent mt-4">Lire <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </Container>
      </div>
    </>
  );
}

const INTERNAL_LINK_RULES = [
  { page: "annonces", re: /(annonces?|biens? à vendre|opportunités?|nos biens)/i },
  { page: "expertises", re: /(expertises?|estimations?|estimer)/i },
  { page: "monreseau", re: /(réseau)/i },
  { page: "partenaires", re: /(partenaires?|artisans?|courtiers?)/i },
  { page: "contact", re: /(contactez|contacter|prendre rendez-vous|contact)/i },
  { page: "assistant", re: /(acquéreurs?|recherche de bien)/i },
];

// Maillage interne : transforme la 1ʳᵉ occurrence de mots-clés en liens dorés vers les pages du site.
function renderWithLinks(text, slug, used) {
  let best = null;
  for (const rule of INTERNAL_LINK_RULES) {
    if (used.has(rule.page)) continue;
    const m = rule.re.exec(text);
    if (m && (best === null || m.index < best.index)) best = { index: m.index, match: m[0], page: rule.page };
  }
  if (!best) return [text];
  used.add(best.page);
  const before = text.slice(0, best.index);
  const after = text.slice(best.index + best.match.length);
  return [
    before,
    <Link key={best.page} to={`/${slug}/site/${best.page}`} className="txt-accent font-medium underline decoration-[color:var(--sp)]/40 underline-offset-2 hover:opacity-80 transition-opacity">{best.match}</Link>,
    ...renderWithLinks(after, slug, used),
  ];
}

const ProseLight = ({ text, slug }) => {
  const used = new Set();
  const paras = (text || "").split(/\n{2,}|\n/).filter(Boolean);
  return (
    <div className="space-y-4">
      {paras.map((p, i) => (
        <motion.p key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.35) }} className="text-base font-light text-zinc-700 leading-relaxed">
          {renderWithLinks(p, slug, used)}
        </motion.p>
      ))}
    </div>
  );
};

function NewsArticle({ news = [], article, slug }) {
  const a = news.find((x) => x.slug === article);
  if (!a) return <div className="pt-40 pb-24 bg-[#F4F5F7]"><Container><p className="text-zinc-500">Article introuvable. <Link to={`/${slug}/site/news`} className="txt-accent">Retour aux News</Link></p></Container></div>;
  return (
    <div className="bg-[#F4F5F7] pt-32 pb-24" data-testid="site-news-article">
      <Container className="max-w-4xl">
        <Link to={`/${slug}/site/news`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 mb-5 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Toutes les news</Link>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest mb-3"><span className="txt-accent">{a.tag}</span><span className="text-zinc-400">{a.date}</span></div>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-zinc-900 mb-7 leading-tight">{a.title}</h1>
        <div className="after:content-[''] after:block after:clear-both">
          {a.cover && <img src={a.cover} alt={a.title} className="w-full md:float-right md:w-[46%] md:ml-8 mb-6 rounded-[5px] aspect-[4/3] object-cover shadow-lg" data-testid="site-news-article-cover" />}
          <ProseLight text={a.body} slug={slug} />
        </div>
      </Container>
    </div>
  );
}

function ReseauJoin({ m, primary, join = {} }) {
  const [open, setOpen] = useState(false);
  const [joinType, setJoinType] = useState("");
  const advisorName = m.company || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.display_name;
  return (
    <>
      <JoinContent advisorName={advisorName} accent={primary || "#C19A6B"} coverImage={join.cover || ""} headline={join.headline || ""} subtitle={join.subtitle || ""} onJoin={(t) => { setJoinType(t || ""); setOpen(true); }} />
      <JoinModal open={open} onClose={() => setOpen(false)} referrerCode={m.referral_code || ""} referrerName={advisorName} defaultType={joinType} />
    </>
  );
}

// Champ message avec dictée vocale (Whisper) + amélioration IA (Claude), thème sombre.
function AIMessageField({ value, onChange, error }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [improving, setImproving] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    setAiErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "message.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", body: fd });
          if (!res.ok) throw new Error("transcription");
          const d = await res.json();
          onChange((value ? value + " " : "") + (d.text || ""));
        } catch (_) { setAiErr("Transcription impossible, réessayez ou saisissez au clavier."); }
        setTranscribing(false);
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (_) { setAiErr("Micro indisponible. Autorisez l'accès au micro ou saisissez au clavier."); }
  };
  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };
  const improve = async () => {
    if (value.trim().length < 5) { setAiErr("Écrivez d'abord quelques mots à améliorer."); return; }
    setAiErr(""); setImproving(true);
    try { const d = await reformulateMessage(value); if (d.text) onChange(d.text); }
    catch (e) { setAiErr(e.message || "Amélioration impossible."); }
    setImproving(false);
  };

  return (
    <div>
      <div className="relative border border-white/15 bg-white/5 focus-within:border-[color:var(--sp)] transition-colors" data-testid="site-contact-message-box">
        <textarea
          className="w-full min-h-[160px] resize-none bg-transparent px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none"
          placeholder="Votre message * — écrivez, ou dictez avec le micro puis laissez l'IA l'améliorer."
          value={value} onChange={(e) => onChange(e.target.value)} data-testid="site-contact-message" />
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-white/10">
          <span className="text-[11px] text-zinc-500 inline-flex items-center gap-2">{transcribing ? "Transcription…" : recording ? <><SoundWave className="text-red-500" /> Enregistrement…</> : improving ? "Amélioration IA…" : "Dictez ou améliorez votre message"}</span>
          <div className="flex items-center gap-2">
            {!recording ? (
              <motion.button type="button" onClick={startRec} disabled={transcribing} data-testid="site-contact-mic"
                animate={{ scale: [1, 1.06, 1], y: [0, -2, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.08 }}
                style={{ boxShadow: "0 0 16px rgba(193,154,107,0.55)" }}
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider bg-[#C19A6B] hover:bg-[#A88358] text-white px-4 py-2 rounded-[5px] transition-colors disabled:opacity-50">
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />} Dicter
              </motion.button>
            ) : (
              <button type="button" onClick={stopRec} data-testid="site-contact-mic-stop"
                className="inline-flex items-center gap-2 text-[11px] font-medium bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-[5px] transition-colors">
                <SoundWave className="text-white" bars={5} /> Arrêter
              </button>
            )}
            <button type="button" onClick={improve} disabled={improving || transcribing} data-testid="site-contact-improve"
              className="inline-flex items-center gap-1.5 text-[11px] font-medium btn-accent px-3 py-1.5 transition-luxury disabled:opacity-50">
              {improving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Améliorer mon message
            </button>
          </div>
        </div>
      </div>
      {aiErr && <p className="text-[11px] text-amber-400 mt-1.5">{aiErr}</p>}
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
    </div>
  );
}

function Contact({ m, slug, waNumber, banner }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.name || !f.email || !f.message) { setErr("Merci de renseigner nom, e-mail et message."); return; }
    setLoading(true);
    try { await submitSiteContact(slug, f); setSent(true); } catch (ex) { setErr(ex.message); }
    setLoading(false);
  };
  const inp = "w-full bg-white border border-zinc-200 rounded-[5px] px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 outline-none focus:border-[color:var(--sp)] transition-colors";
  const contactRows = [
    m.company && { icon: Building2, label: "Enseigne", value: m.company },
    m.phone && { icon: Phone, label: "Téléphone", value: m.phone, href: `tel:${m.phone.replace(/\s/g, "")}` },
    m.email && { icon: Mail, label: "E-mail", value: m.email, href: `mailto:${m.email}` },
    (m.address || m.city) && { icon: MapPin, label: "Adresse", value: m.address || m.city },
    m.website && { icon: Globe, label: "Site", value: m.website, href: m.website.startsWith("http") ? m.website : `https://${m.website}` },
  ].filter(Boolean);
  return (
    <>
      <PageBanner {...banner} subtitle={banner.subtitle || "Une question, un projet de vente ou d'achat ? Écrivez-moi, je vous réponds rapidement."} />
      <div className="sp-sec sp-sec-white py-20 sm:py-28">
        <Container>
          <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }} className="mb-12 max-w-2xl">
            <span className="calli text-3xl sm:text-4xl txt-accent">Parlons de votre projet</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 text-zinc-900">Restons en contact</h2>
          </motion.div>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Coordonnées — sans cadres, sur fond blanc */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="lg:col-span-5 space-y-7">
              <div className="space-y-5">
                {contactRows.map((r, i) => {
                  const Inner = (
                    <>
                      <r.icon className="w-5 h-5 shrink-0 txt-accent" />
                      <span><span className="block text-[10px] uppercase tracking-widest text-zinc-400">{r.label}</span><span className="block text-sm text-zinc-900 font-light">{r.value}</span></span>
                    </>
                  );
                  return r.href
                    ? <a key={i} href={r.href} target={r.icon === Globe ? "_blank" : undefined} rel="noopener noreferrer" className="group flex items-center gap-3.5 hover:opacity-80 transition-opacity">{Inner}</a>
                    : <div key={i} className="group flex items-center gap-3.5">{Inner}</div>;
                })}
              </div>
              <div className="flex flex-wrap gap-3">
                {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="btn-accent rounded-[5px] px-6 py-3 text-sm uppercase tracking-wider transition-luxury inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Appeler</a>}
                {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="border bd-accent txt-accent rounded-[5px] px-6 py-3 text-sm uppercase tracking-wider hover:bg-[color:var(--sp)]/5 transition-luxury">WhatsApp</a>}
              </div>
              <div className="h-56 rounded-[5px] overflow-hidden" data-testid="site-contact-map">
                <LeafletMiniMap query={`${m.address || m.city || m.sector || ""}`} label={m.company || `${m.first_name || ""} ${m.last_name || ""}`.trim()} zoom={12} className="w-full h-full" />
              </div>
            </motion.div>

            {/* Formulaire — sans cadre classique, sur fond gris clair arrondi 5px */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: 0.12 }} className="lg:col-span-7 bg-zinc-100 rounded-[5px] p-6 sm:p-9">
              {sent ? (
                <div className="p-10 text-center" data-testid="site-contact-sent">
                  <Check className="w-12 h-12 mx-auto mb-4 txt-accent" />
                  <p className="text-lg font-light text-zinc-900">Merci ! Votre message a bien été envoyé.</p>
                  <p className="text-sm text-zinc-500 mt-2">Je vous recontacte dans les meilleurs délais.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="site-contact-form">
                  <div className="calli text-2xl txt-accent mb-1">Écrivez-moi</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={inp} placeholder="Votre nom *" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="site-contact-name" />
                    <input className={inp} type="email" placeholder="Votre e-mail *" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="site-contact-email" />
                  </div>
                  <input className={inp} placeholder="Téléphone" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="site-contact-phone" />
                  <AIMessageField value={f.message} onChange={(v) => set("message", v)} error={err} />
                  <button disabled={loading} className="btn-accent inline-flex items-center gap-2 px-8 py-4 rounded-[5px] text-sm font-medium uppercase tracking-wider transition-luxury disabled:opacity-60" data-testid="site-contact-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer mon message
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </Container>
      </div>
    </>
  );
}
